import { Globe, Copy, Check, Download, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const WordPressStep = ({ data, apiKeyCopied, setApiKeyCopied }) => {
  const handleCopyApiKey = () => {
    if (data?.wordpress_api_key) {
      navigator.clipboard.writeText(data.wordpress_api_key);
      setApiKeyCopied(true);
      setTimeout(() => setApiKeyCopied(false), 3000);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="bg-gradient-to-br from-blue-100 to-blue-200 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
          <Globe className="h-8 w-8 text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">WordPress Integration</h2>
        <p className="text-gray-600">
          Display and sell memberships directly on your WordPress website
        </p>
        <Badge variant="outline" className="mt-2">
          Optional - Can be configured later
        </Badge>
      </div>

      {/* Benefits */}
      <Card className="border-2 border-blue-200 bg-blue-50">
        <CardContent className="pt-6">
          <h3 className="font-semibold text-blue-900 mb-3">Why Connect WordPress?</h3>
          <ul className="space-y-2 text-sm text-blue-800">
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-0.5">✓</span>
              <span><strong>24/7 Online Signups</strong> - Patients can join anytime</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-0.5">✓</span>
              <span><strong>Automatic Sync</strong> - Members added directly to your dashboard</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-0.5">✓</span>
              <span><strong>Beautiful Display</strong> - Professional membership cards on your site</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-0.5">✓</span>
              <span><strong>Reduced Admin Work</strong> - Less manual data entry</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Setup Instructions */}
      <Card className="border-2">
        <CardContent className="pt-6">
          <h3 className="font-semibold text-gray-900 mb-4">Setup Instructions</h3>
          
          <div className="space-y-4">
            {/* Step 1: API Key */}
            <div className="flex gap-3">
              <div className="bg-teal-100 text-teal-700 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                1
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900 mb-2">Copy Your API Key</p>
                {data?.wordpress_api_key ? (
                  <div className="flex gap-2">
                    <code className="flex-1 bg-gray-100 p-3 rounded text-xs font-mono break-all">
                      {data.wordpress_api_key}
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCopyApiKey}
                      className="flex-shrink-0"
                    >
                      {apiKeyCopied ? (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-amber-600">
                    API key will be generated when you complete setup in Settings
                  </p>
                )}
              </div>
            </div>

            {/* Step 2: Download Plugin */}
            <div className="flex gap-3">
              <div className="bg-teal-100 text-teal-700 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                2
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900 mb-2">Download WordPress Plugin</p>
                <Button size="sm" variant="outline" disabled>
                  <Download className="h-4 w-4 mr-2" />
                  Download Plugin (Coming Soon)
                </Button>
              </div>
            </div>

            {/* Step 3: Install */}
            <div className="flex gap-3">
              <div className="bg-teal-100 text-teal-700 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                3
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900 mb-2">Install on WordPress</p>
                <ol className="text-sm text-gray-600 space-y-1 ml-4 list-decimal">
                  <li>Go to WordPress Admin → Plugins → Add New</li>
                  <li>Click "Upload Plugin" and select downloaded file</li>
                  <li>Activate the plugin</li>
                </ol>
              </div>
            </div>

            {/* Step 4: Configure */}
            <div className="flex gap-3">
              <div className="bg-teal-100 text-teal-700 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                4
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900 mb-2">Configure Plugin</p>
                <ol className="text-sm text-gray-600 space-y-1 ml-4 list-decimal">
                  <li>Go to WordPress Admin → MyMedMember Settings</li>
                  <li>Paste your API key</li>
                  <li>Save settings</li>
                </ol>
              </div>
            </div>

            {/* Step 5: Add Shortcode */}
            <div className="flex gap-3">
              <div className="bg-teal-100 text-teal-700 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                5
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900 mb-2">Display on Your Site</p>
                <p className="text-sm text-gray-600 mb-2">Add this shortcode to any page:</p>
                <code className="block bg-gray-100 p-2 rounded text-xs font-mono">
                  [mymedmember_plans]
                </code>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Skip Option */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Don't Have WordPress?</h4>
        <p className="text-sm text-gray-600 mb-3">
          No problem! You can manage member signups manually from your dashboard. WordPress integration is completely optional and can be set up anytime from Settings.
        </p>
        <div className="flex gap-2">
          <Button size="sm" variant="outline">
            <ExternalLink className="h-4 w-4 mr-2" />
            Manual Signup Guide
          </Button>
        </div>
      </div>

      {/* Help Link */}
      <div className="text-center">
        <p className="text-sm text-gray-500">
          Need help? Check out our{' '}
          <a href="#" className="text-teal-600 hover:underline">
            WordPress Integration Guide
          </a>
        </p>
      </div>
    </div>
  );
};

export default WordPressStep;
